import StatusPill from '@/components/console/status-pill';
import PageHeader from '@/components/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import CopyButton from '@/pages/admin/wirings/components/copy-button';
import ProviderCard from '@/pages/admin/wirings/components/provider-card';
import type { WiringProvider } from '@/types/platform';
import type { ReactNode } from 'react';

interface Props {
  providers: WiringProvider[];
  demo: boolean;
  signupOpen: boolean;
  baseUrl: string;
  urls: {
    webhooks: { stripe: string; stripeConnect: string; square: string };
    callbacks: { stripe: string; square: string; quickbooks: string };
  };
  mail: { from: string; deliveries: boolean };
  models: { free: string; paid: string | null };
}

interface UrlEntry {
  label: string;
  url: string;
  // The env name the provider's signing secret for this URL goes into.
  secret?: string;
}

const Show = ({ providers, demo, signupOpen, baseUrl, urls, mail, models }: Props) => {
  const liveCount = providers.filter((provider) => provider.mode === 'live').length;

  const webhooks: UrlEntry[] = [
    {
      label: 'Stripe Billing webhook',
      url: urls.webhooks.stripe,
      secret: 'STRIPE_WEBHOOK_SECRET',
    },
    {
      label: 'Stripe Connect webhook',
      url: urls.webhooks.stripeConnect,
      secret: 'STRIPE_CONNECT_WEBHOOK_SECRET',
    },
    {
      label: 'Square webhook',
      url: urls.webhooks.square,
      secret: 'SQUARE_WEBHOOK_SIGNATURE_KEY',
    },
  ];

  const callbacks: UrlEntry[] = [
    { label: 'Stripe Connect OAuth return URL', url: urls.callbacks.stripe },
    { label: 'Square OAuth return URL', url: urls.callbacks.square },
    { label: 'QuickBooks OAuth return URL', url: urls.callbacks.quickbooks },
  ];

  return (
    <div className="space-y-8">
      <PageHeader
        title="Wiring"
        description="The checklist for plugging in real keys. This page shows env names and whether each is set. Values never leave the server."
        badges={
          <StatusPill tone={liveCount === providers.length ? 'success' : 'neutral'}>
            {liveCount} of {providers.length} live
          </StatusPill>
        }
      />

      <p className="text-sm">
        The step-by-step is in <code className="text-xs">docs/wiring.md</code> in the repo.
      </p>

      <section aria-labelledby="wiring-providers" className="space-y-4">
        <h2 id="wiring-providers" className="text-lg font-semibold">
          Providers
        </h2>
        <div className="grid gap-4 lg:grid-cols-2">
          {providers.map((provider) => (
            <ProviderCard key={provider.key} provider={provider} />
          ))}
        </div>
      </section>

      <section aria-labelledby="wiring-urls" className="space-y-4">
        <h2 id="wiring-urls" className="text-lg font-semibold">
          URLs to register
        </h2>
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>
                <h3>Webhooks</h3>
              </CardTitle>
              <CardDescription>
                Register each URL with its provider, then put the signing secret the provider gives
                you into the env name shown.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <UrlList entries={webhooks} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                <h3>OAuth return URLs</h3>
              </CardTitle>
              <CardDescription>
                Where each provider sends an owner back after they approve a connection.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <UrlList entries={callbacks} />
            </CardContent>
          </Card>
        </div>
      </section>

      <section aria-labelledby="wiring-flags" className="space-y-4">
        <h2 id="wiring-flags" className="text-lg font-semibold">
          Flags and settings
        </h2>
        <Card>
          <CardContent>
            <dl className="divide-y text-sm">
              <Setting
                name="Demo mode"
                env="PLATFORM_DEMO_MODE"
                value={
                  demo ? <StatusPill tone="warning">On</StatusPill> : <StatusPill>Off</StatusPill>
                }
                note={
                  demo
                    ? 'A provider with no keys runs as a stand-in and says so.'
                    : 'A provider with no keys reports that it is not connected.'
                }
              />
              <Setting
                name="Public signup"
                env="PUBLIC_SIGNUP_ENABLED"
                value={
                  signupOpen ? (
                    <StatusPill tone="success">Open</StatusPill>
                  ) : (
                    <StatusPill>Closed</StatusPill>
                  )
                }
              />
              <Setting
                name="Base URL"
                value={<code className="text-xs break-all">{baseUrl}</code>}
                note="Used for links in mail and for published app addresses."
              />
              <Setting
                name="Mail from"
                env="MAIL_FROM"
                value={<span className="break-all">{mail.from}</span>}
              />
              <Setting
                name="Mail deliveries"
                env="SEND_EMAILS"
                value={
                  mail.deliveries ? (
                    <StatusPill tone="success">On</StatusPill>
                  ) : (
                    <StatusPill tone="warning">Off</StatusPill>
                  )
                }
                note="In development, mail goes out only when SEND_EMAILS is true and RESEND_API_KEY is set."
              />
              <Setting
                name="Draft model, free"
                env="OPENROUTER_MODEL"
                value={<code className="text-xs break-all">{models.free}</code>}
              />
              <Setting
                name="Draft model, paid"
                env="OPENROUTER_PAID_MODEL"
                value={
                  models.paid ? (
                    <code className="text-xs break-all">{models.paid}</code>
                  ) : (
                    <span className="text-yellow-800">
                      not set; exception retries are unavailable
                    </span>
                  )
                }
              />
            </dl>
          </CardContent>
        </Card>
      </section>
    </div>
  );
};

const UrlList = ({ entries }: { entries: UrlEntry[] }) => {
  return (
    <ul className="divide-y text-sm">
      {entries.map((entry) => (
        <li key={entry.label} className="space-y-1.5 py-3 first:pt-0 last:pb-0">
          <div className="flex items-center justify-between gap-3">
            <span className="font-medium">{entry.label}</span>
            <CopyButton value={entry.url} label={entry.label} />
          </div>
          <code className="bg-muted block rounded px-2 py-1 text-xs break-all">{entry.url}</code>
          {entry.secret ? (
            <p className="text-muted-foreground text-xs">
              Signing secret goes in <code className="text-foreground">{entry.secret}</code>
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
};

interface SettingProps {
  name: string;
  env?: string;
  value: ReactNode;
  note?: string;
}

const Setting = ({ name, env, value, note }: SettingProps) => {
  return (
    <div className="grid gap-1 py-3 first:pt-0 last:pb-0 sm:grid-cols-[14rem_1fr] sm:gap-4">
      <dt>
        <span className="font-medium">{name}</span>
        {env ? <code className="text-muted-foreground block text-xs break-all">{env}</code> : null}
      </dt>
      <dd className="min-w-0">
        {value}
        {note ? <p className="text-muted-foreground mt-1 text-xs">{note}</p> : null}
      </dd>
    </div>
  );
};

export default Show;
